import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, apiFetchBlob } from '../api/client';
import type {
  NdaBindResponse,
  NdaCaptureConfigUpdate,
  NdaExecutionActionResponse,
  NdaExecutionCandidatesResponse,
  NdaIntakeActionResponse,
  NdaIntakeCandidatesResponse,
  NdaIntakeDecisionsResponse,
  NdaReverseDecisionResponse,
  NdaQueuesSummary,
  NdaDocketResponse,
  NdaErrorsResponse,
  NdaExecutionDetail,
  NdaHealthResponse,
  NdaLinksResponse,
  NdaLinksUpdate,
  NdaObligationActionResponse,
  NdaObligationEdit,
  NdaOrgSearchResponse,
  NdaReviewDetail,
  NdaSettings,
  NdaStatusResponse,
} from '../types';

/**
 * NDA Reviewer tab hooks (build plan 1.11, spec §12.1).
 *
 * Read-only Phase 1 — queries only, no mutations. Every endpoint is
 * server-gated to nda_privileged_emails (403 otherwise); these hooks
 * surface that as a query error rather than retrying.
 */

export function useNdaDocket() {
  return useQuery<NdaDocketResponse>({
    queryKey: ['nda', 'docket'],
    queryFn: () => apiFetch<NdaDocketResponse>('/api/nda/reviews'),
    staleTime: 30 * 1000,
  });
}

export function useNdaReviewDetail(reviewId: number | null) {
  return useQuery<NdaReviewDetail>({
    queryKey: ['nda', 'review', reviewId],
    queryFn: () => apiFetch<NdaReviewDetail>(`/api/nda/reviews/${reviewId}`),
    enabled: reviewId != null,
    staleTime: 30 * 1000,
  });
}

/**
 * Executed-NDA closing review (PR 6B-2): the active execution summary + its
 * source-anchored obligations + the projected lifecycle status. Server-gated to
 * nda_privileged_emails.
 */
export function useNdaExecution(reviewId: number | null) {
  return useQuery<NdaExecutionDetail>({
    queryKey: ['nda', 'execution', reviewId],
    queryFn: () => apiFetch<NdaExecutionDetail>(`/api/nda/reviews/${reviewId}/execution`),
    enabled: reviewId != null,
    staleTime: 30 * 1000,
  });
}

/** Invalidate every view that a closing-review mutation can change. */
function useInvalidateClosingReview() {
  const qc = useQueryClient();
  return (reviewId: number) => {
    qc.invalidateQueries({ queryKey: ['nda', 'execution', reviewId] });
    qc.invalidateQueries({ queryKey: ['nda', 'review', reviewId] });
    qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
  };
}

/** Confirm extracted obligations — all (ids omitted) or a selected subset. */
export function useConfirmObligations(reviewId: number) {
  const invalidate = useInvalidateClosingReview();
  return useMutation({
    mutationFn: (obligationIds?: number[]) =>
      apiFetch<NdaObligationActionResponse>(`/api/nda/reviews/${reviewId}/obligations/confirm`, {
        method: 'POST',
        body: JSON.stringify({ obligation_ids: obligationIds ?? null }),
      }),
    onSuccess: () => invalidate(reviewId),
  });
}

/** Edit the structured projection over one obligation (source evidence immutable). */
export function useEditObligation(reviewId: number) {
  const invalidate = useInvalidateClosingReview();
  return useMutation({
    mutationFn: ({ obligationId, edit }: { obligationId: number; edit: NdaObligationEdit }) =>
      apiFetch<NdaObligationActionResponse>(
        `/api/nda/reviews/${reviewId}/obligations/${obligationId}`,
        { method: 'PATCH', body: JSON.stringify(edit) },
      ),
    onSuccess: () => invalidate(reviewId),
  });
}

/** Un-confirm a confirmed obligation (confirmed → extracted). */
export function useRevertObligation(reviewId: number) {
  const invalidate = useInvalidateClosingReview();
  return useMutation({
    mutationFn: (obligationId: number) =>
      apiFetch<NdaObligationActionResponse>(
        `/api/nda/reviews/${reviewId}/obligations/${obligationId}/revert`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidate(reviewId),
  });
}

/** Confirm the captured executed copy: executed_unconfirmed → executed (PR 6A-2). */
export function useConfirmExecution(reviewId: number) {
  const invalidate = useInvalidateClosingReview();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/api/nda/reviews/${reviewId}/execution/confirm`, { method: 'POST' }),
    onSuccess: () => invalidate(reviewId),
  });
}

/** Revert a mis-captured execution (supersedes summary + obligations). */
export function useRevertExecution(reviewId: number) {
  const invalidate = useInvalidateClosingReview();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/api/nda/reviews/${reviewId}/execution/revert`, { method: 'POST' }),
    onSuccess: () => invalidate(reviewId),
  });
}

// The first-turn calibration corpus hook (useNdaCorpus) was removed in PR-5c
// along with NdaFirstTurnComparison. The /corpus endpoints + capture stay
// server-side (the tuning corpus is still built); there's just no UI reader.

export function useNdaHealth() {
  return useQuery<NdaHealthResponse>({
    queryKey: ['nda', 'health'],
    queryFn: () => apiFetch<NdaHealthResponse>('/api/nda/health'),
    staleTime: 30 * 1000,
  });
}

export function useNdaErrors() {
  return useQuery<NdaErrorsResponse>({
    queryKey: ['nda', 'errors'],
    queryFn: () => apiFetch<NdaErrorsResponse>('/api/nda/errors'),
    staleTime: 30 * 1000,
  });
}

/**
 * Archive / unarchive a review (PR 6). Soft-delete out of the active docket;
 * reversible. Invalidates the docket so the row moves between the active list
 * and the Archived panel.
 */
export function useArchiveReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      apiFetch(`/api/nda/reviews/${id}/${archived ? 'archive' : 'unarchive'}`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nda', 'docket'] }),
  });
}

/**
 * Bind a review to a deal (slice 3 manual bind). Mirrors useArchiveReview but
 * invalidates BOTH the docket (label changes) AND the open review detail.
 */
export function useBindReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dealKind, dealKey }: { id: number; dealKind: string; dealKey: string }) =>
      apiFetch<NdaBindResponse>(`/api/nda/reviews/${id}/bind`, {
        method: 'POST',
        body: JSON.stringify({ deal_kind: dealKind, deal_key: dealKey }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
      qc.invalidateQueries({ queryKey: ['nda', 'review', vars.id] });
    },
  });
}

/** CRM org typeahead for the link-edit modal (PR-2b). Server-gated to
 *  nda_privileged; returns [] for a short/empty query or a non-CRM principal. */
export function useNdaOrgSearch(query: string) {
  const q = (query || '').trim();
  return useQuery({
    queryKey: ['nda', 'org-search', q],
    queryFn: () => apiFetch<NdaOrgSearchResponse>(
      `/api/nda/org-search?q=${encodeURIComponent(q)}&limit=12`),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

/** Manual override of a review's CRM entity links (PR-2b). Every provided field
 *  is written AND locked so the auto-resolver won't clobber it. */
export function useUpdateReviewLinks(reviewId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NdaLinksUpdate) =>
      apiFetch<NdaLinksResponse>(`/api/nda/reviews/${reviewId}/links`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nda', 'review', reviewId] });
      qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
    },
  });
}

/** Unbind a review (slice 3). Clears (deal_kind, deal_key) back to unbound. */
export function useUnbindReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<NdaBindResponse>(`/api/nda/reviews/${id}/unbind`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
      qc.invalidateQueries({ queryKey: ['nda', 'review', id] });
    },
  });
}

/** Manually set a review's lifecycle status (open|executed|concluded|dismissed). */
export function useUpdateReviewStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      apiFetch<NdaStatusResponse>(`/api/nda/reviews/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
      qc.invalidateQueries({ queryKey: ['nda', 'review', vars.id] });
    },
  });
}

/** GET the admin-editable GC auto-inclusion policy (PR 6). */
export function useNdaSettings() {
  return useQuery<NdaSettings>({
    queryKey: ['nda', 'settings'],
    queryFn: () => apiFetch<NdaSettings>('/api/nda/settings'),
    staleTime: 60 * 1000,
  });
}

/**
 * PATCH NDA settings — the GC auto-inclusion policy and/or the capture/
 * notification config. Both arms optional; send only what changed.
 */
export function useUpdateNdaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      gc_auto_inclusion?: string;
      capture?: NdaCaptureConfigUpdate;
    }) =>
      apiFetch<NdaSettings>('/api/nda/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nda', 'settings'] }),
  });
}

// ── PR-4 execution-candidate confirm queue ──────────────────────────────────

/** Pending-work counts for the docket queue banner. */
export function useNdaQueuesSummary() {
  return useQuery<NdaQueuesSummary>({
    queryKey: ['nda', 'queues', 'summary'],
    queryFn: () => apiFetch<NdaQueuesSummary>('/api/nda/queues/summary'),
    staleTime: 30 * 1000,
  });
}

/** Pending executed-copy candidates awaiting a human confirm. */
export function useNdaExecutionCandidates() {
  return useQuery<NdaExecutionCandidatesResponse>({
    queryKey: ['nda', 'execution-candidates'],
    queryFn: () => apiFetch<NdaExecutionCandidatesResponse>('/api/nda/execution-candidates'),
    staleTime: 30 * 1000,
  });
}

function useInvalidateQueues() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['nda', 'execution-candidates'] });
    qc.invalidateQueries({ queryKey: ['nda', 'queues', 'summary'] });
    qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
  };
}

/** Confirm an executed copy: capture it (→ obligations extraction) onto a matched
 * matter (`review_id`) or a new backload matter (`create_matter`). */
export function useConfirmExecutionCandidate() {
  const invalidate = useInvalidateQueues();
  return useMutation({
    mutationFn: ({
      workItemId,
      review_id,
      create_matter,
    }: {
      workItemId: number;
      review_id?: number;
      create_matter?: boolean;
    }) =>
      apiFetch<NdaExecutionActionResponse>(
        `/api/nda/execution-candidates/${workItemId}/confirm`,
        { method: 'POST', body: JSON.stringify({ review_id, create_matter }) },
      ),
    // onSettled (not onSuccess): a concurrent 409 must also refetch so the
    // now-resolved candidate drops out of the list.
    onSettled: invalidate,
  });
}

/** Dismiss an executed-copy candidate (not an executed NDA / wrong pick). */
export function useDismissExecutionCandidate() {
  const invalidate = useInvalidateQueues();
  return useMutation({
    mutationFn: (workItemId: number) =>
      apiFetch<NdaExecutionActionResponse>(
        `/api/nda/execution-candidates/${workItemId}/dismiss`,
        { method: 'POST' },
      ),
    onSettled: invalidate,
  });
}

/** Detected NDAs the intake matcher matched to >1 open matter, held for a human (PR-8b). */
export function useNdaIntakeCandidates() {
  return useQuery<NdaIntakeCandidatesResponse>({
    queryKey: ['nda', 'intake-candidates'],
    queryFn: () => apiFetch<NdaIntakeCandidatesResponse>('/api/nda/intake-candidates'),
    staleTime: 30 * 1000,
  });
}

function useInvalidateIntake() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['nda', 'intake-candidates'] });
    qc.invalidateQueries({ queryKey: ['nda', 'queues', 'summary'] });
    qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
  };
}

/** Attach a held detected NDA to a chosen open matter (append as a revision turn). */
export function useAttachIntakeCandidate() {
  const invalidate = useInvalidateIntake();
  return useMutation({
    mutationFn: ({
      workItemId,
      review_id,
      learn_alias,
    }: {
      workItemId: number;
      review_id: number;
      learn_alias?: boolean;
    }) =>
      apiFetch<NdaIntakeActionResponse>(
        `/api/nda/intake-candidates/${workItemId}/attach`,
        { method: 'POST', body: JSON.stringify({ review_id, learn_alias: !!learn_alias }) },
      ),
    onSettled: invalidate,
  });
}

/** Resolve a held detected NDA as a NEW review (operator asserts it is a new NDA). */
export function useCreateNewIntakeCandidate() {
  const invalidate = useInvalidateIntake();
  return useMutation({
    mutationFn: (workItemId: number) =>
      apiFetch<NdaIntakeActionResponse>(
        `/api/nda/intake-candidates/${workItemId}/create-new`,
        { method: 'POST' },
      ),
    onSettled: invalidate,
  });
}

// ── PR-8d: intake DECISIONS review + reverse/re-route ────────────────────────

/** Recent CONFIDENT drain intake decisions (auto-binds) for operator review. */
export function useNdaIntakeDecisions() {
  return useQuery<NdaIntakeDecisionsResponse>({
    queryKey: ['nda', 'intake-decisions'],
    queryFn: () => apiFetch<NdaIntakeDecisionsResponse>('/api/nda/intake-decisions'),
    staleTime: 30 * 1000,
  });
}

/**
 * Reverse a wrong auto-bind + re-route the source. `destination` is
 * hold | attach | create_new | noise; `target_review_id` is required for attach;
 * `learn_alias` teaches the codename→matter alias on a re-bind. Invalidates the
 * decisions list, the docket, and both re-routed matters' detail views.
 */
export function useReverseIntakeDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      spineId,
      destination,
      target_review_id,
      learn_alias,
    }: {
      spineId: number;
      destination: string;
      target_review_id?: number;
      learn_alias?: boolean;
    }) =>
      apiFetch<NdaReverseDecisionResponse>(
        `/api/nda/intake-decisions/${spineId}/reverse`,
        {
          method: 'POST',
          body: JSON.stringify({ destination, target_review_id, learn_alias: !!learn_alias }),
        },
      ),
    onSettled: (data) => {
      qc.invalidateQueries({ queryKey: ['nda', 'intake-decisions'] });
      qc.invalidateQueries({ queryKey: ['nda', 'docket'] });
      qc.invalidateQueries({ queryKey: ['nda', 'queues', 'summary'] });
      qc.invalidateQueries({ queryKey: ['nda', 'intake-candidates'] });
      if (data?.review_id) qc.invalidateQueries({ queryKey: ['nda', 'review', data.review_id] });
      if (data?.reversed_from_review_id)
        qc.invalidateQueries({ queryKey: ['nda', 'review', data.reversed_from_review_id] });
    },
  });
}

/**
 * Audited artifact download. The backend writes an nda_audit row
 * (action='artifact_downloaded') before returning bytes; this helper
 * just streams the blob into a browser save-as.
 */
export async function downloadNdaArtifact(
  reviewId: number,
  artifactId: number,
  filename: string | null,
): Promise<void> {
  const blob = await apiFetchBlob(
    `/api/nda/reviews/${reviewId}/artifacts/${artifactId}/download`,
  );
  _saveBlob(blob, filename || `nda-artifact-${artifactId}`);
}

/**
 * Audited document-version download (PR-5a). The backend re-checks the current
 * source permission class (a post-capture privacy escalation → 403) and writes
 * an nda_audit row before returning bytes.
 */
export async function downloadNdaDocument(
  reviewId: number,
  documentId: number,
  filename: string | null,
): Promise<void> {
  const blob = await apiFetchBlob(
    `/api/nda/reviews/${reviewId}/documents/${documentId}/download`,
  );
  _saveBlob(blob, filename || `nda-document-${documentId}`);
}

function _saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
